import React from 'react'
import {
    Grid,
    Button,
    FormControlLabel,
    Checkbox,
    Typography,
    Box
} from '@mui/material'

export type PermissionFamily =
    | 'audience'
    | 'campaigns'
    | 'inventory'
    | 'programs'
    | 'whatsapp'
    | 'studio'
    | 'dial_campaigns'
    | 'offline_campaigns'
    | 'users'

export type PermissionType = 'read' | 'write' | 'delete' | 'update'

export interface IUserPermissions {
    audience: Record<PermissionType, boolean>;
    campaigns: Record<PermissionType, boolean>;
    inventory: Record<PermissionType, boolean>;
    programs: Record<PermissionType, boolean>;
    whatsapp: Record<PermissionType, boolean>;
    studio: Record<PermissionType, boolean>;
    dial_campaigns: Record<PermissionType, boolean>;
    offline_campaigns: Record<PermissionType, boolean>;
    users: Record<PermissionType, boolean>;
}

export const permissionFamilies: PermissionFamily[] = [
    'audience',
    'campaigns',
    'inventory',
    'programs',
    'whatsapp',
    'studio',
    'dial_campaigns',
    'offline_campaigns',
    'users'
]

export const permissionTypes: PermissionType[] = ['read', 'write', 'delete', 'update']

export const familyLabels: Record<PermissionFamily, string> = {
    audience: 'Audiência',
    campaigns: 'Campanhas',
    inventory: 'Inventário',
    programs: 'Programas',
    whatsapp: 'WhatsApp',
    studio: 'Studio',
    dial_campaigns: 'Comprova Play',
    offline_campaigns: 'Campanhas Offline',
    users: 'Usuários'
}

export const permissionLabels: Record<PermissionType, string> = {
    read: 'Ler',
    update: 'Editar',
    write: 'Criar',
    delete: 'Apagar'
}

export const defaultPermissions: IUserPermissions = {
    audience: { read: false, write: false, delete: false, update: false },
    campaigns: { read: false, write: false, delete: false, update: false },
    inventory: { read: false, write: false, delete: false, update: false },
    programs: { read: false, write: false, delete: false, update: false },
    whatsapp: { read: false, write: false, delete: false, update: false },
    studio: { read: false, write: false, delete: false, update: false },
    dial_campaigns: { read: false, write: false, delete: false, update: false },
    offline_campaigns: { read: false, write: false, delete: false, update: false },
    users: { read: false, write: false, delete: false, update: false }
}

interface UserPermissionsEditorProps {
    permissions: IUserPermissions
    onChange: (permissions: IUserPermissions) => void
    /** Famílias que ficam travadas (disabled), ex.: ['users'] */
    disabledFamilies?: PermissionFamily[]
}

const UserPermissionsEditor: React.FC<UserPermissionsEditorProps> = ({
    permissions,
    onChange,
    disabledFamilies = []
}) => {
    console.log('[DEBUG UserPermissionsEditor] render, permissions =', JSON.stringify(permissions))

    return (
    <Grid item xs={12}>
        <Box display="flex" flexDirection="column" gap={1}>
            {permissionFamilies.map(family => {
                const allChecked = Object.values(permissions[family]).every(v => v)
                const isDisabledFamily = disabledFamilies.includes(family)

                return (
                    <Box
                        key={family}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            transition: 'border-color 0.2s, background-color 0.2s',
                            opacity: isDisabledFamily ? 0.5 : 1,
                            backgroundColor: allChecked && !isDisabledFamily ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                            '&:hover': {
                                borderColor: isDisabledFamily ? 'divider' : 'primary.main'
                            }
                        }}
                    >
                        <Box sx={{ minWidth: 140, flexShrink: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {familyLabels[family]}
                            </Typography>
                            {isDisabledFamily && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    Somente para administradores
                                </Typography>
                            )}
                        </Box>

                        <Button
                            id={`${family}_all_permissions_button`}
                            size="small"
                            sx={{ minWidth: 0, px: 1.5, py: 0.25, fontSize: 11 }}
                            variant={allChecked ? 'contained' : 'outlined'}
                            color="primary"
                            disabled={isDisabledFamily}
                            onClick={() => {
                                console.log('[DEBUG UserPermissionsEditor] clicou "Todos" da família:', family, '-> allChecked antes:', allChecked)
                                onChange({
                                    ...permissions,
                                    [family]: {
                                        read: !allChecked,
                                        write: !allChecked,
                                        delete: !allChecked,
                                        update: !allChecked
                                    }
                                })
                            }}
                        >
                            Todos
                        </Button>

                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {permissionTypes.map(type => (
                                <FormControlLabel
                                    key={`${family}-${type}`}
                                    sx={{ mr: 1.5 }}
                                    disabled={isDisabledFamily}
                                    control={
                                        <Checkbox
                                            id={`${family}_${type}_permission_checkbox`}
                                            size="small"
                                            checked={permissions[family][type]}
                                            disabled={isDisabledFamily}
                                            onChange={e => {
                                                console.log('[DEBUG UserPermissionsEditor] checkbox', family, type, '-> novo valor:', e.target.checked)
                                                onChange({
                                                    ...permissions,
                                                    [family]: {
                                                        ...permissions[family],
                                                        [type]: e.target.checked
                                                    }
                                                })
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body2">{permissionLabels[type]}</Typography>}
                                />
                            ))}
                        </Box>
                    </Box>
                )
            })}
        </Box>
    </Grid>
    )
}

export default UserPermissionsEditor
